import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { sortBy } from 'lodash';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';
import RichTextArea from 'components/common/richTextArea/richTextArea';

import { userMentionMapper } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/EditNarrative/editNarrative';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useCustomReportTextLogic } from 'components/widgets/CustomReportText/CustomReportText.logic';
import classes from 'components/widgets/CustomReportText/CustomReportText.module.scss';
import { useCustomReportTextProvider } from 'components/widgets/CustomReportText/CustomReportText.provider';

const EDITOR_DEFAULT_HEIGHT = 450;
const { id, componentKey, endpoint } = widgetsConfigurations.customReportText;

export const CustomReportText = ({
    widgetId,
    externalComponents = null,
    reportWidgetRef = null,
    widgetViewMode = WidgetView.page,
    localFilters,
    title = widgetsConfigurations.customReportText.title,
}: IComponentProps) => {
    const { ui, api } = useCustomReportTextProvider({
        widgetViewMode,
        widgetId,
        componentKey,
        localFilters,
    });

    const { editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        title,
        ui,
        reportWidgetRef,
        widgetEndpoint: endpoint,
    });

    const { logic } = useCustomReportTextLogic({ ui });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                        />
                    ),
                ]}
            >
                <div className={classes.customReportTextContainer}>
                    {WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) ? (
                        <RichTextArea
                            onEditorDescription={logic.onEditorDescription}
                            existingData={ui.state.description}
                            editorContentHeight={EDITOR_DEFAULT_HEIGHT}
                            isAutoFocus={true}
                            loadingText={'Enter your text here.'}
                            isClearButton
                            mention={{
                                separator: ' ',
                                trigger: '@',
                                suggestions:
                                    sortBy(
                                        api.assigneeList.data?.map(userMentionMapper),
                                        [(o) => o.text.toLowerCase()],
                                        ['asc'],
                                    ) || [],
                            }}
                        />
                    ) : logic.hasTextContent(ui.state.description) ? (
                        <div
                            className={classes.viewMode}
                            dangerouslySetInnerHTML={{ __html: ui.state.description }}
                        ></div>
                    ) : null}
                </div>
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
